import {
  Person,
  PersonCardInteraction,
  PersonViewType,
} from '@microsoft/mgt-react';
import {
  AppSubscriptionItem,
  ManageUserPanelContainer,
  SubscriptionItem,
} from './styles';
import { IUserDocument } from '../UserSettingsTable';
import { EntObject } from '../../pages/MyHub';
import { Dropdown, FluentProvider, Option } from '@fluentui/react-components';
import { Icon } from '@fluentui/react';
import { AppObject } from '../AppCard';
import { getTheme } from '../../../styles/themes';
import { useTranslation } from 'react-i18next';

interface ManageUserPanelProps {
  themeName: string;
  selectedUser?: IUserDocument;
  entitlements: EntObject;
  apps: AppObject[];
  currentUser: string;
  onComboChanged(subscriptionId: string, name: string, option: string): void;
}

export const ManageUserPanel = ({
  themeName,
  selectedUser,
  entitlements,
  currentUser,
  onComboChanged,
  apps,
}: ManageUserPanelProps) => {
  const activeTheme = getTheme(themeName);

  const { t } = useTranslation('settings');

  const assignmentTypes = [
    { value: 'none', label: 'None' },
    { value: 'owner', label: 'Owner', disabled: true },
    { value: 'admin', label: 'Admin' },
    { value: 'user', label: 'User' },
  ];

  const assignmentTypesNoLicence = [
    { value: 'none', label: 'None' },
    { value: 'owner', label: 'Owner', disabled: true },
    { value: 'admin', label: 'Admin', disabled: true },
    { value: 'user', label: 'User', disabled: true },
  ];

  return (
    <FluentProvider theme={activeTheme.newFluent}>
      <ManageUserPanelContainer>
        <Person
          className={themeName !== 'default' ? 'mgt-dark' : ''}
          userId={selectedUser?.key}
          view={PersonViewType.twolines}
          showPresence
          personCardInteraction={PersonCardInteraction.hover}
          line2Property="email"
        />
        <h1>{t('PANEL_USER_LICENCES_SUBTITLE')}</h1>

        {selectedUser &&
          entitlements.subscriptions.map((sub) => {
            const subscription = JSON.parse(JSON.stringify(sub));

            const isOwner =
              selectedUser.key === subscription.beneficiary ? true : false;

            const isAdmin = subscription.adminUsers.find(
              (usr: string) => usr === selectedUser.key
            )
              ? true
              : false;
            const isUser = subscription.users.find(
              (usr: string) => usr === selectedUser.key
            )
              ? true
              : false;
            const currIsAdmin = subscription.adminUsers.find(
              (usr: string) => usr === currentUser
            )
              ? true
              : false;
            const licenseAvailable =
              subscription.users.length < subscription.quantity || isUser
                ? true
                : false;
            const selectedIndex = isOwner
              ? 'owner'
              : isAdmin
              ? 'admin'
              : isUser
              ? 'user'
              : 'none';
            const isTrial = subscription.isFreeTrial;

            return (
              <SubscriptionItem>
                <div className="subscriptionHeader">
                  <h2>{subscription.name}</h2>
                  <p>
                    {subscription.users.length}{' '}
                    {t('SUBSCRIPTIONS_LICENCES_AMOUNT')} {subscription.quantity}{' '}
                    {t('SUBSCRIPTIONS_LICENCES_ASSIGNED')}
                  </p>
                </div>

                {!isTrial && (
                  <div className="subscriptionType">
                    <h3>{t('PANEL_USER_LICENCE_ASSIGNED_AS')}</h3>
                    <Dropdown
                      defaultSelectedOptions={[selectedIndex]}
                      placeholder={selectedIndex}
                      disabled={isOwner || !currIsAdmin}
                      appearance="underline"
                      onOptionSelect={(ev, op) =>
                        onComboChanged(
                          subscription.id,
                          subscription.name,
                          op.optionValue as string
                        )
                      }
                    >
                      {licenseAvailable
                        ? assignmentTypes.map((type) => (
                            <Option
                              key={'assigment-' + type.value}
                              value={type.value}
                              disabled={type.disabled}
                            >
                              {type.label}
                            </Option>
                          ))
                        : assignmentTypesNoLicence.map((type) => (
                            <Option
                              key={'assigment-' + type.value}
                              value={type.value}
                              disabled={type.disabled}
                            >
                              {type.label}
                            </Option>
                          ))}
                    </Dropdown>
                  </div>
                )}

                <div className="apps">
                  <h3>{t('PANEL_USER_SUBSCRIPTION_APPS')}</h3>
                  {subscription.planId.entitlements.map((ent: string) => {
                    const entitlement = JSON.parse(JSON.stringify(ent));
                    const app = apps.find(
                      (value) => value.id === entitlement.appid
                    );

                    if (app) {
                      return (
                        <AppSubscriptionItem>
                          <Icon iconName={app.iconName} />
                          <h3>{app.name}</h3>
                          <p>
                            {entitlement.type}: {entitlement.value}
                          </p>
                        </AppSubscriptionItem>
                      );
                    }
                  })}
                </div>
              </SubscriptionItem>
            );
          })}
      </ManageUserPanelContainer>
    </FluentProvider>
  );
};
